import { type FC, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { UsageBreakdowns } from "../../types/usage";

import { useTheme } from "../../context/ThemeContext";
import { getChartTheme } from "../../theme/chartTheme";
import { formatTokenCount } from "../../utils/currency";
import { ContentCard } from "../common/ContentCard";
import { SectionHeader } from "../common/SectionHeader";
import { Text } from "../common/Text";

export interface UsageTokenCompositionChartProps {
  breakdowns: UsageBreakdowns | null;
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

interface TokenCompositionRow {
  model: string;
  prompt: number;
  completion: number;
  cached: number;
  cacheWrite: number;
}

function buildTokenComposition(
  breakdowns: UsageBreakdowns,
): TokenCompositionRow[] {
  return breakdowns.byModel
    .map((row) => ({
      cacheWrite: row.totals.cacheWriteTokens,
      cached: row.totals.cachedInputTokens,
      completion: row.totals.completionTokens,
      model: row.model ?? "Unknown",
      prompt: row.totals.promptTokens,
    }))
    .toSorted(
      (a, b) =>
        b.prompt +
        b.completion +
        b.cached +
        b.cacheWrite -
        (a.prompt + a.completion + a.cached + a.cacheWrite),
    );
}

/* ------------------------------------------------------------------ */
/*  Tooltip                                                            */
/* ------------------------------------------------------------------ */

interface CompositionTooltipPayloadEntry {
  name: string;
  value: number;
  color: string;
}

interface CompositionTooltipProps {
  active?: boolean;
  payload?: CompositionTooltipPayloadEntry[];
  label?: string;
  isDark: boolean;
}

const CompositionTooltip: FC<CompositionTooltipProps> = ({
  active,
  payload,
  label,
  isDark,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div
      className="rounded-none border px-2.5 py-1.5 text-xs shadow-sm"
      style={{
        backgroundColor: getChartTheme(isDark).tooltipBg,
        borderColor: getChartTheme(isDark).tooltipBorder,
        color: getChartTheme(isDark).tooltipText,
      }}
    >
      <div
        className="mb-1"
        style={{ color: getChartTheme(isDark).tooltipMuted }}
      >
        {label}
      </div>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-1.5">
          <div
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span>
            {entry.name}:{" "}
            <span className="font-semibold">
              {formatTokenCount(entry.value)}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const AXIS_TICK_STYLE = { fontSize: 10 };

export const UsageTokenCompositionChart: FC<
  UsageTokenCompositionChartProps
> = ({ breakdowns }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const data = useMemo(
    () => (breakdowns ? buildTokenComposition(breakdowns) : []),
    [breakdowns],
  );

  if (data.length === 0) {
    return null;
  }

  const { axisText, gridLine } = getChartTheme(isDark);
  const cursorFill = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)";

  const promptFill = isDark
    ? "rgba(148, 163, 184, 0.60)"
    : "rgba(71, 85, 105, 0.72)";
  const completionFill = isDark
    ? "rgba(52, 211, 153, 0.60)"
    : "rgba(16, 185, 129, 0.70)";
  const cachedFill = isDark
    ? "rgba(251, 113, 133, 0.60)"
    : "rgba(225, 29, 72, 0.72)";
  const cacheWriteFill = isDark
    ? "rgba(245, 158, 11, 0.60)"
    : "rgba(217, 119, 6, 0.70)";

  return (
    <div className="space-y-3">
      <SectionHeader title="Token Composition" />
      <ContentCard className="space-y-3">
        <Text variant="label" as="div">
          Tokens by model
        </Text>
        <ResponsiveContainer
          width="100%"
          height={Math.max(160, data.length * 40)}
        >
          <BarChart
            data={data}
            layout="vertical"
            margin={{ bottom: 0, left: 0, right: 4, top: 4 }}
          >
            <CartesianGrid horizontal={false} stroke={gridLine} />
            <XAxis
              type="number"
              tick={AXIS_TICK_STYLE}
              stroke={axisText}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => formatTokenCount(v)}
            />
            <YAxis
              type="category"
              dataKey="model"
              width={100}
              tick={AXIS_TICK_STYLE}
              stroke={axisText}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              cursor={{ fill: cursorFill }}
              content={<CompositionTooltip isDark={isDark} />}
            />
            <Legend
              iconType="rect"
              iconSize={8}
              wrapperStyle={{ color: axisText, fontSize: 11 }}
            />
            <Bar
              name="Prompt"
              dataKey="prompt"
              stackId="tokens"
              fill={promptFill}
              maxBarSize={20}
            />
            <Bar
              name="Completion"
              dataKey="completion"
              stackId="tokens"
              fill={completionFill}
              maxBarSize={20}
            />
            <Bar
              name="Cached"
              dataKey="cached"
              stackId="tokens"
              fill={cachedFill}
              maxBarSize={20}
            />
            <Bar
              name="Cache write"
              dataKey="cacheWrite"
              stackId="tokens"
              radius={[0, 4, 4, 0]}
              fill={cacheWriteFill}
              maxBarSize={20}
            />
          </BarChart>
        </ResponsiveContainer>
      </ContentCard>
    </div>
  );
};
